"use client";

import * as React from "react";

import { Button, buttonVariants } from "@unreserved/ui/components/button";
import { cn } from "@unreserved/utils";
import { type VariantProps } from "class-variance-authority";

import { LoaderIcon } from "@/app/_components/icons";

interface AppButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "size" | "children">,
    Omit<VariantProps<typeof buttonVariants>, "size"> {
  text: React.ReactNode;
  isLoading?: boolean;
  asChild?: boolean;
  size?: "sm" | "md" | "lg" | "default" | "icon" | "icon-sm" | "icon-lg";
}

export const AppButton = React.forwardRef<HTMLButtonElement, AppButtonProps>(
  (
    {
      text,
      isLoading = false,
      disabled,
      className,
      variant = "default",
      size = "default",
      type = "button",
      asChild = false,
      ...props
    },
    ref,
  ) => {
    const isDisabled = disabled || isLoading;

    const mappedSize = size === "md" ? "default" : size;

    const sizeStyles = {
      sm: "px-4 py-2 text-sm h-auto",
      default: "px-6 py-3 text-base h-auto",
      md: "px-6 py-3 text-base h-auto",
      lg: "px-6 py-4 text-lg h-auto",
    };

    const customStyles =
      variant === "default"
        ? cn(
            "bg-primary-500 hover:bg-primary-600 text-typography-0 rounded-lg font-medium",
            sizeStyles[size as keyof typeof sizeStyles] || sizeStyles.default,
          )
        : "";

    return (
      <Button
        ref={ref}
        type={type}
        disabled={isDisabled}
        variant={variant}
        size={mappedSize as VariantProps<typeof buttonVariants>["size"]}
        asChild={asChild}
        className={cn(customStyles, className)}
        {...props}>
        {isLoading && (
          <LoaderIcon
            className={cn("animate-spin", size === "sm" ? "h-4 w-4" : "h-5 w-5")}
            aria-hidden="true"
          />
        )}
        {text}
      </Button>
    );
  },
);

AppButton.displayName = "AppButton";
